import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getAvailableModels, 
  getCurrentModel, 
  switchModel, 
  clearModelCache 
} from '../services/api';
import { 
  CpuChipIcon, 
  ArrowPathIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const ModelSwitcher = ({ showAsCard = true, className = '' }) => {
  const queryClient = useQueryClient();
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  // Fetch available models
  const { data: availableModels, isLoading: modelsLoading } = useQuery({
    queryKey: ['availableModels'],
    queryFn: getAvailableModels,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch current model info
  const { data: currentModel, isLoading: currentLoading } = useQuery({
    queryKey: ['currentModel'],
    queryFn: getCurrentModel,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Switch model mutation
  const switchModelMutation = useMutation({
    mutationFn: ({ provider, modelId }) => switchModel(provider, modelId),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['currentModel']);
      setSelectedProvider('');
      setSelectedModel('');
    },
    onError: (error) => {
      console.error('Failed to switch model:', error);
    }
  });

  // Clear cache mutation
  const clearCacheMutation = useMutation({
    mutationFn: clearModelCache,
    onSuccess: () => {
      queryClient.invalidateQueries(['currentModel']);
    }
  });

  // Set initial values when current model data loads
  useEffect(() => {
    if (currentModel?.model_info) {
      setSelectedProvider(currentModel.model_info.provider || '');
      setSelectedModel(currentModel.model_info.model_id || '');
    }
  }, [currentModel]);

  const handleProviderChange = (provider) => {
    setSelectedProvider(provider);
    // Reset model selection when provider changes
    setSelectedModel('');
  };

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
  };

  const handleSwitchModel = async () => {
    if (!selectedProvider || !selectedModel) return;
    
    // Check if this is already the current model
    if (currentModel?.model_info?.provider === selectedProvider && 
        currentModel?.model_info?.model_id === selectedModel) {
      return;
    }

    switchModelMutation.mutate({
      provider: selectedProvider,
      modelId: selectedModel
    });
  };

  const handleClearCache = () => {
    clearCacheMutation.mutate();
  };

  const getModelDisplayName = (modelId) => {
    // Shorten long model IDs for display
    if (modelId?.length > 30) {
      return modelId.substring(0, 27) + '...';
    }
    return modelId;
  };

  const isCurrentModel = currentModel?.model_info?.provider === selectedProvider && 
                         currentModel?.model_info?.model_id === selectedModel;

  if (modelsLoading || currentLoading) {
    return (
      <div className={`animate-pulse ${showAsCard ? 'bg-gray-50 p-4 rounded-lg' : ''} ${className}`}>
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-8 bg-gray-200 rounded mb-2"></div>
        <div className="h-8 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const content = (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <CpuChipIcon className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">Model Selection</h3>
        </div>
        <button
          onClick={handleClearCache}
          disabled={clearCacheMutation.isPending}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
          title="Clear model cache"
        >
          <TrashIcon className="h-4 w-4 mr-1" />
          Clear Cache
        </button>
      </div>

      {/* Current Model Status */}
      {currentModel?.model_info && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Current: {currentModel.model_info.provider} / {getModelDisplayName(currentModel.model_info.model_id)}
              </p>
              <p className="text-xs text-blue-700">
                Cache: {currentModel.model_info.cache_size} models loaded
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Provider Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Provider
        </label>
        <select
          value={selectedProvider}
          onChange={(e) => handleProviderChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        >
          <option value="">Select Provider</option>
          {availableModels?.models && Object.keys(availableModels.models).map(provider => (
            <option key={provider} value={provider}>
              {provider.charAt(0).toUpperCase() + provider.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Model Selection */}
      {selectedProvider && availableModels?.models?.[selectedProvider] && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model
          </label>
          <select
            value={selectedModel}
            onChange={(e) => handleModelChange(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">Select Model</option>
            {availableModels.models[selectedProvider].map(modelId => (
              <option key={modelId} value={modelId}>
                {modelId}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Switch Button */}
      {selectedProvider && selectedModel && (
        <div className="pt-2">
          <button
            onClick={handleSwitchModel}
            disabled={switchModelMutation.isPending || isCurrentModel}
            className={`w-full flex items-center justify-center px-4 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isCurrentModel
                ? 'border-green-300 text-green-700 bg-green-50 cursor-not-allowed'
                : 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 focus:ring-blue-500'
            } disabled:opacity-50`}
          >
            {switchModelMutation.isPending ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Switching...
              </>
            ) : isCurrentModel ? (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                Current Model
              </>
            ) : (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Switch Model
              </>
            )}
          </button>
        </div>
      )}

      {/* Error Display */}
      {switchModelMutation.isError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <p className="text-sm font-medium text-red-800">Failed to switch model</p>
              <p className="text-xs text-red-600 mt-1">
                {switchModelMutation.error?.response?.data?.detail || switchModelMutation.error?.message || 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {switchModelMutation.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            <div>
              <p className="text-sm font-medium text-green-800">Model switched successfully</p>
              <p className="text-xs text-green-600 mt-1">
                Now using {selectedProvider} / {getModelDisplayName(selectedModel)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (showAsCard) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
        {content}
      </div>
    );
  }

  return (
    <div className={className}>
      {content}
    </div>
  );
};

export default ModelSwitcher;